import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Logo } from '@/components/shared/Logo'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const email = searchParams.get('email')
  const token = searchParams.get('token')

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  if (!email || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-8">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6"><Logo size={44} theme="light" /></div>
          <p className="text-slate-700 font-medium mb-2">Invalid reset link</p>
          <p className="text-slate-500 text-sm mb-6">This reset link is invalid. Please request a new one.</p>
          <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            Request a new link
          </Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    setLoading(true)
    try {
      await api.post('/auth/reset-password', { email, token, newPassword })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 1500)
    } catch (err: any) {
      setError(
        err.response?.data?.detail ??
        'This link has expired or is invalid. Please request a new one.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-8">
      <div className="w-full max-w-sm">
        <div className="mb-6"><Logo size={44} theme="light" /></div>

        {success ? (
          <>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Password updated</h2>
            <p className="text-slate-500 text-sm">Password updated. Redirecting…</p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Choose a new password</h2>
            <p className="text-slate-500 text-sm mb-8">Enter a new password for <strong>{email}</strong>.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="At least 8 characters"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {error}{' '}
                  {error.includes('expired') && (
                    <Link to="/forgot-password" className="underline font-medium">
                      Request a new one.
                    </Link>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Updating…' : 'Update password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
